import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Sparkle, CheckCircle, PencilSimple, CalendarPlus, FileText, ArrowsClockwise, Archive } from "@phosphor-icons/react";

function ChannelBadge({ ch }) {
  return <span className="text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded border border-neutral-300 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400 dark:text-neutral-500">{ch}</span>;
}

function ConfidencePill({ c }) {
  if (c == null) return null;
  const pct = Math.round(c * 100);
  const low = c < 0.6;
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded border ${low ? "border-[#FF3B30] text-[#FF3B30]" : "border-neutral-300 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400 dark:text-neutral-500"}`}>
      AI {pct}%
    </span>
  );
}

function IntentTag({ intent }) {
  if (!intent) return null;
  return <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded bg-neutral-950 text-white dark:bg-neutral-100 dark:text-neutral-950">{intent.replace(/_/g, " ")}</span>;
}

export default function Inbox() {
  const [convs, setConvs] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const loadList = useCallback(async () => {
    const r = await api.get("/conversations", { params: { archived: showArchived ? "true" : "false" } });
    setConvs(r.data);
    setSelectedId((prev) => (r.data.some((c) => c.conversation_id === prev) ? prev : (r.data[0]?.conversation_id || null)));
  }, [showArchived]);
  const loadOne = useCallback(async (id) => {
    const r = await api.get(`/conversations/${id}`);
    setSelected(r.data);
    setDraft(r.data.ai_suggested_reply || "");
  }, []);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (selectedId) loadOne(selectedId); }, [selectedId, loadOne]);

  const approveSend = async () => {
    if (!draft.trim()) return;
    setSending(true);
    try {
      await api.post(`/conversations/${selectedId}/reply`, { body: draft });
      toast.success("Reply sent");
      await loadOne(selectedId);
      await loadList();
    } catch { toast.error("Failed to send"); }
    finally { setSending(false); }
  };

  const rerun = async () => {
    setBusy(true);
    try {
      await api.post(`/conversations/${selectedId}/reanalyse`);
      await loadOne(selectedId);
      toast.success("AI re-analysed");
    } catch { toast.error("Failed to re-analyse"); }
    finally { setBusy(false); }
  };

  const suggestSlot = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const s = await api.post("/appointments/suggest", { count: 3 });
      const slot = s.data.slots[0];
      if (!slot) { toast.error("No slots available"); return; }
      await api.post("/appointments", {
        customer_id: selected.customer_id,
        title: `Appointment - ${selected.subject || selected.customer_name}`,
        start: slot.start,
        end: slot.end,
        conversation_id: selected.conversation_id,
      });
      toast.success("Appointment drafted (awaiting approval)");
    } catch { toast.error("Could not draft appointment"); }
    finally { setBusy(false); }
  };

  const draftQuote = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await api.post("/quotes/draft-from-conversation", { conversation_id: selected.conversation_id });
      toast.success(`Quote drafted: $${r.data.total.toFixed(2)}`);
    } catch { toast.error("Could not draft quote"); }
    finally { setBusy(false); }
  };

  const toggleArchive = async () => {
    if (!selected) return;
    const isArch = Boolean(selected.archived);
    try {
      await api.post(`/conversations/${selected.conversation_id}/${isArch ? "unarchive" : "archive"}`);
      toast(isArch ? "Restored from archive" : "Archived");
      setSelected(null);
      await loadList();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="flex min-h-screen">
      {/* List */}
      <div className="w-96 border-r border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
        <div className="p-5 border-b border-neutral-200 dark:border-neutral-800">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.28em] text-neutral-500 dark:text-neutral-500">Inbox</div>
              <h1 className="text-2xl font-semibold tracking-tight mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>{showArchived ? "Archived" : "Conversations"}</h1>
            </div>
            <button
              data-testid="inbox-toggle-archived"
              onClick={() => setShowArchived((v) => !v)}
              className="text-xs px-2 py-1 rounded-md border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800"
              title={showArchived ? "Show active" : "Show archived"}
            >
              {showArchived ? "Active" : "Archive"}
            </button>
          </div>
        </div>
        <div className="divide-y divide-neutral-100 dark:divide-neutral-800 max-h-[calc(100vh-100px)] overflow-y-auto">
          {convs.length === 0 && (
            <div className="p-6 text-sm text-neutral-500 dark:text-neutral-500">No conversations yet. Head to the dashboard to simulate an enquiry.</div>
          )}
          {convs.map((c) => (
            <button
              key={c.conversation_id}
              data-testid={`conv-item-${c.conversation_id}`}
              onClick={() => setSelectedId(c.conversation_id)}
              className={`w-full text-left p-4 hover:bg-neutral-50 dark:hover:bg-neutral-800/60 transition-colors ${selectedId === c.conversation_id ? "bg-neutral-100 dark:bg-neutral-800" : ""}`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm font-medium truncate">{c.customer_name}</div>
                <ChannelBadge ch={c.channel} />
              </div>
              <div className="text-xs text-neutral-500 dark:text-neutral-500 truncate">{c.subject}</div>
              <div className="flex items-center gap-1.5 mt-2">
                <IntentTag intent={c.ai_intent} />
                <ConfidencePill c={c.ai_confidence} />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Detail */}
      <div className="flex-1 min-w-0 p-8">
        {!selected ? (
          <div className="text-sm text-neutral-500 dark:text-neutral-500">Select a conversation</div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} key={selected.conversation_id}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <ChannelBadge ch={selected.channel} />
                  <IntentTag intent={selected.ai_intent} />
                  <ConfidencePill c={selected.ai_confidence} />
                </div>
                <h2 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>{selected.customer_name}</h2>
                <div className="text-sm text-neutral-500 dark:text-neutral-500">{selected.customer_email || "—"}</div>
              </div>
              <div className="flex gap-2">
                <button data-testid="rerun-ai-btn" onClick={rerun} disabled={busy} className="text-xs px-3 py-2 rounded-md border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 flex items-center gap-1"><ArrowsClockwise size={14} /> Re-run AI</button>
                <button data-testid="suggest-slot-btn" onClick={suggestSlot} disabled={busy} className="text-xs px-3 py-2 rounded-md border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 flex items-center gap-1"><CalendarPlus size={14} /> Draft appointment</button>
                <button data-testid="draft-quote-btn" onClick={draftQuote} disabled={busy} className="text-xs px-3 py-2 rounded-md border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 flex items-center gap-1"><FileText size={14} /> Draft quote</button>
                <button data-testid="archive-conv-btn" onClick={toggleArchive} className="text-xs px-3 py-2 rounded-md border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 flex items-center gap-1"><Archive size={14} /> {selected.archived ? "Restore" : "Archive"}</button>
              </div>
            </div>

            <div className="mt-6 border border-neutral-200 dark:border-neutral-800 rounded-lg bg-white dark:bg-neutral-900 p-5">
              <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-500 mb-2 flex items-center gap-2"><Sparkle size={14} weight="fill" className="text-[#0F52BA]" /> AI summary</div>
              <div className="text-sm text-neutral-800 dark:text-neutral-200">{selected.ai_summary || "—"}</div>
              {selected.ai_reasoning && <div className="mt-2 text-xs text-neutral-500 dark:text-neutral-500">Why: {selected.ai_reasoning}</div>}
            </div>

            <div className="mt-6 border border-neutral-200 dark:border-neutral-800 rounded-lg bg-white dark:bg-neutral-900 p-5">
              <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-500 mb-3">Transcript</div>
              <div className="space-y-3">
                {selected.messages.map((m) => (
                  <div key={m.message_id} className={`text-sm ${m.sender === "customer" ? "" : "pl-8"}`}>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-500 mb-1">
                      {m.sender === "customer" ? m.sender_name : `Us · ${m.sender_name}`} · {new Date(m.created_at).toLocaleString()}
                    </div>
                    <div className={`p-3 rounded-md ${m.sender === "customer" ? "bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-800" : "bg-[#0F52BA]/5 border border-[#0F52BA]/20"}`}>
                      {m.body}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 border border-neutral-200 dark:border-neutral-800 rounded-lg bg-white dark:bg-neutral-900 p-5">
              <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-500 mb-2 flex items-center gap-2"><PencilSimple size={14} /> AI-drafted reply (approve or edit before sending)</div>
              <textarea
                data-testid="reply-draft-textarea"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={6}
                className="w-full text-sm p-3 border border-neutral-300 dark:border-neutral-700 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F52BA]/40"
              />
              <div className="mt-3 flex gap-2 justify-end">
                <button
                  data-testid="approve-send-btn"
                  onClick={approveSend}
                  disabled={sending || !draft.trim()}
                  className="text-sm px-4 py-2 rounded-md bg-[#0F52BA] hover:bg-[#0C4094] disabled:opacity-60 text-white flex items-center gap-2"
                >
                  <CheckCircle size={16} weight="fill" /> {sending ? "Sending…" : "Approve & send"}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
